import CloseRounded from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { storeBrand, toggleFormBrands, updateBrand } from '../../../../../redux/actions/brands'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import TabPanel from '../../../../shared/components/TabPanel'
import { CancelButton, Dropzone, RemoveContent, SaveButton } from '../../../../shared/components/inputs'
import 'react-dropzone-uploader/dist/styles.css'
import { useTranslation } from 'react-i18next'
import InputColor from 'react-input-color'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { formsStyles } from '../../../../shared/styles/FormsStyles'

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

interface IBrand {
  id: number
  families: number[]
  name: null | string
  description: null | string
  light_logo: null | string
  dark_logo: null | string
  main_color: any
  secondary_color: any
  background: null | string
  lines: any
}

const filter = createFilterOptions()

function BrandsModal() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const theme = useTheme()
  const formClasses = formsStyles(theme)

  const dispatch = useDispatch()
  const { brandSelected, showForm } = useSelector((state: IRootState) => state.brand)
  const { families } = useSelector((state: IRootState) => state.family)
  const { isValidForm, errorForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)

  const [brand, setBrand] = useState<IBrand>({
    id: 0,
    families: [],
    name: null,
    description: null,
    light_logo: null,
    dark_logo: null,
    main_color: { hex: '#5e72e4' },
    secondary_color: { hex: '#5e72e4' },
    background: null,
    lines: []
  })

  const [currentTab, setCurrentTab] = useState(0)

  useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())

      if (brand.id === 0) dispatch(storeBrand(brand))
      else dispatch(updateBrand(brand))
    }
  }, [isValidForm])

  useEffect(() => {
    if (brandSelected !== null && brandSelected.id !== brand.id) {
      setBrand({
        id: brandSelected.id,
        families:
          brandSelected.familiesIds.length > 0
            ? brandSelected.familiesIds
            : brandSelected.family_id !== null
              ? [brandSelected.family_id]
              : [],
        // families: [],
        name: brandSelected.name,
        description: brandSelected.description,
        light_logo: brandSelected.light_logo,
        dark_logo: brandSelected.dark_logo,
        main_color: { hex: brandSelected.main_color },
        secondary_color: { hex: brandSelected.secondary_color },
        background: brandSelected.background,
        lines: brandSelected.lines
      })
    }
  }, [brandSelected])

  /* Functions */
  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const handleInputChange = (inputName: string, value: any) => {
    setBrand(prev => ({ ...prev, [inputName]: value }))
  }

  const handleMultiAutoCompleteChange = (inputName: string, value: any) => {
    let optionsMulti: number[] = []

    if (value !== null) {
      for (let i = 0; i < value.length; i++) {
        optionsMulti.push(value[i].id)
      }
    }

    setBrand({ ...brand, [inputName]: optionsMulti })
  }

  const handleSubmit = () => {
    dispatch(
      formValidator({
        families: [brand.families, 'notempty'],
        name: [brand.name, 'required'],
        description: [brand.description, 'required|max:500|min:3', 'addOn'],
        dark_logo: [brand.dark_logo, 'required', 'general'],
        light_logo: [brand.light_logo, 'required', 'general'],
        main_color: [brand.main_color, 'required', 'general'],
        background: [brand.background, 'required', 'general']
      })
    )
  }

  const handleCloseModalBrand = () => {
    dispatch(resetFormValidator())
    dispatch(toggleFormBrands())
  }

  return (
    <>
      <Dialog
        open={showForm}
        fullWidth={true}
        fullScreen={isMobile && true}
        maxWidth={!isMobile && 'md'}
        data-testid="form-brand"
        sx={formClasses.containerForm}
        onClose={handleCloseModalBrand}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="form-dialog-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">
              {brandSelected !== null ? t('create') : t('edit')} {t('brand')}
            </Typography>
            <Button onClick={handleCloseModalBrand} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>

        <DialogContent sx={formClasses.bodyForm}>
          <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                autoFocus
                id="brand-name"
                label={`${t('name')}*`}
                type="text"
                variant="filled"
                value={brand.name ?? ''}
                error={errorForm.name}
                helperText={msgForm.name}
                InputLabelProps={{ shrink: true }}
                onChange={e => handleInputChange('name', e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                id="combo-box-families"
                value={families.filter((family: any) => brand.families.includes(family.id))}
                onChange={(_event, value) => handleMultiAutoCompleteChange('families', value)}
                options={families}
                multiple
                getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                renderInput={params => (
                  <TextField
                    required
                    {...params}
                    error={errorForm.families}
                    label={t('family')}
                    variant="filled"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <FormHelperText error={errorForm.families}>{msgForm.families}</FormHelperText>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Tabs
              value={currentTab}
              indicatorColor="secondary"
              textColor="primary"
              onChange={handleChangeTab}
              sx={formClasses.tapForm}
              aria-label="disabled tabs example"
            >
              <Tab label={t('general')} {...a11yProps(0)} sx={errorTab.general ? formClasses.errorTab : undefined} />
              <Tab label={t('add-ons')} {...a11yProps(1)} sx={errorTab.addOn ? formClasses.errorTab : undefined} />
            </Tabs>
          </Grid>

          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {`${t('light-logo')}*`}
                </Typography>
                {brand.light_logo ? (
                  <Box my={2}>
                    <RemoveContent
                      files={[`${import.meta.env.VITE_AWS_URL}/brands/${brand.light_logo}`]}
                      onHandleClick={() => handleInputChange('light_logo', null)}
                      darkMode
                    />
                  </Box>
                ) : (
                  <Dropzone name={'light_logo'} handleInputChange={handleInputChange} dir={'brands'} />
                )}
                <FormHelperText error={errorForm.light_logo}>{msgForm.light_logo}</FormHelperText>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {`${t('dark-logo')}*`}
                </Typography>
                {brand.dark_logo ? (
                  <Box my={2}>
                    <RemoveContent
                      files={[`${import.meta.env.VITE_AWS_URL}/brands/${brand.dark_logo}`]}
                      onHandleClick={() => handleInputChange('dark_logo', null)}
                    />
                  </Box>
                ) : (
                  <Dropzone name={'dark_logo'} handleInputChange={handleInputChange} dir={'brands'} />
                )}
                <FormHelperText error={errorForm.dark_logo}>{msgForm.dark_logo}</FormHelperText>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {t('main-color')}
                </Typography>
                <div className="input-color">
                  <InputColor initialValue={brand.main_color.hex} onChange={color => handleInputChange('main_color', color)} />
                </div>
                <Typography variant="body2" color="textSecondary">
                  {t('secondary-color')}
                </Typography>
                <div className="input-color">
                  <InputColor initialValue={brand.secondary_color.hex} onChange={color => handleInputChange('secondary_color', color)} />
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  {`${t('background')}*`}
                </Typography>
                {brand.background ? (
                  <Box my={2}>
                    <RemoveContent
                      files={[`${import.meta.env.VITE_AWS_URL}/brands/${brand.background}`]}
                      onHandleClick={() => handleInputChange('background', null)}
                    />
                  </Box>
                ) : (
                  <Dropzone name={'background'} handleInputChange={handleInputChange} dir={'brands'} />
                )}
                <FormHelperText error={errorForm.background}>{msgForm.background}</FormHelperText>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  id="brand-description"
                  label={`${t('description')}*`}
                  multiline
                  variant="filled"
                  value={brand.description ?? ''}
                  error={errorForm.description}
                  helperText={msgForm.description}
                  InputLabelProps={{ shrink: true }}
                  onChange={e => handleInputChange('description', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={brand.lines}
                  onChange={(_event, value) => setBrand({ ...brand, lines: value })}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params)

                    if (params.inputValue !== '') {
                      filtered.push({
                        id: 0,
                        name: `${params.inputValue}`
                      })
                    }

                    return filtered
                  }}
                  id="combo-box-lines"
                  options={brand.lines}
                  getOptionLabel={option => (option.id !== undefined ? option.name : '')}
                  selectOnFocus
                  handleHomeEndKeys
                  renderOption={(props, option) => <li {...props}>{option.name}</li>}
                  multiple
                  renderInput={params => <TextField {...params} label={t('lines')} variant="filled" InputLabelProps={{ shrink: true }} />}
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>

        <DialogActions sx={!isValidForm && Object.keys(msgForm).length > 0 ? formClasses.errorDialogActions : undefined}>
          <CancelButton onClick={handleCloseModalBrand}>{t('cancel')}</CancelButton>
          <SaveButton id="btn-save-brand" onClick={handleSubmit}>
            {t('save')}
          </SaveButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BrandsModal
